import { Icon } from "@circle-react-shared/Icon";
import type { PaywallPrice } from "@circle-react/api/paywallCheckoutApi.types";
import {
  CouponApplicationStatus,
  findPrice,
  useGetCouponApplication,
} from "@circle-react/components/Paywalls/Checkout/hooks/useGetCouponApplication";
import type { CheckoutContextProps } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { PRICE_SCENARIOS } from "@circle-react/helpers/paywallPriceHelpers";
import { compact } from "lodash";

import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";

const getTooltipText = (
  couponDuration: string,
  count: string | number | undefined,
  hasMore: boolean,
) => {
  const locales = compact([
    "paywall_checkout.coupon.coupon_tag",
    couponDuration,
    hasMore && couponDuration != "forever" ? "plus" : null,
  ]);

  return t(locales, { count });
};

const getDiscountCounter = (
  couponApplication: ReturnType<typeof useGetCouponApplication>,
  price?: Pick<PaywallPrice, "scenario">,
) => {
  const couponApplicationCount = Number(couponApplication.application_count);
  let discountsToRemove = 0;

  if (price?.scenario === PRICE_SCENARIOS.value) {
    discountsToRemove = 2;
  }

  if (
    PRICE_SCENARIOS.trialThenValue === price?.scenario ||
    PRICE_SCENARIOS.upfrontThenValue === price?.scenario
  ) {
    discountsToRemove = 2;
  }

  if (
    PRICE_SCENARIOS.trialThenUpfrontThenValue === price?.scenario ||
    PRICE_SCENARIOS.upfrontThenTrialThenValue === price?.scenario
  ) {
    discountsToRemove = 3;
  }

  return Math.max(couponApplicationCount - discountsToRemove, 0);
};

interface CouponTagProps {
  couponPreviewMutation: CheckoutContextProps["couponPreviewMutation"];
  paywallPriceId: number | string;
}

export const CouponTag = ({
  paywallPriceId,
  couponPreviewMutation,
}: CouponTagProps) => {
  const paywallCoupon = couponPreviewMutation?.data?.paywall_coupon;

  const price = findPrice(
    Number(paywallPriceId),
    couponPreviewMutation?.data?.paywall?.prices,
  );
  const couponApplication = useGetCouponApplication(
    Number(paywallPriceId),
    couponPreviewMutation,
  );

  if (
    !paywallCoupon ||
    CouponApplicationStatus.NO_COUPON_APPLIED === couponApplication.status
  ) {
    return null;
  }

  if (
    CouponApplicationStatus.COUPON_NOT_AVAILABLE === couponApplication.status
  ) {
    return (
      <div>
        <TippyV2
          content={t("paywall_checkout.form.errors.coupon_code_not_available")}
          placement="top"
        >
          <Icon type="20-no-tags" />
        </TippyV2>
      </div>
    );
  }

  const locale = getTooltipText(
    paywallCoupon.duration,
    getDiscountCounter(couponApplication, price),
    couponApplication.has_more,
  );

  return (
    <div>
      <TippyV2 content={locale} placement="top">
        <Icon type="20-tags" />
      </TippyV2>
    </div>
  );
};
